import { useCallback, useEffect, useRef, useState } from 'react';
import { FLEET_GROUP_LIMIT, type FleetClient, type FleetGroupClient } from '@galaxis/shared';
import {
  fetchFleetGroups, createFleetGroup, updateFleetGroup, deleteFleetGroup, setFleetGroupMembers, fetchFleets,
} from '../../api/galaxy.ts';
import { useGameStore } from '../../store/game-store.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { newIdempotencyKey } from './fleet-format.ts';

const INPUT = 'rounded-lg bg-slate-900 border border-slate-600 px-3 text-sm text-slate-200 min-w-0';

/** The player's fleet groups. Reloads when a fleet points at a group the list does not know (e.g. created on another device). */
export function useFleetGroups(fleets: FleetClient[]) {
  const [groups, setGroups] = useState<FleetGroupClient[]>([]);
  const loaded = useRef(false);
  const reload = useCallback(async () => {
    try { setGroups((await fetchFleetGroups()).groups); loaded.current = true; } catch { /* groups are optional */ }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  const known = new Set(groups.map(g => g.id));
  const unknown = fleets.some(f => f.groupId && !known.has(f.groupId));
  useEffect(() => { if (unknown && loaded.current) void reload(); }, [unknown, reload]);
  return { groups, setGroups, reload };
}

export function GroupDot({ color, size = 10 }: { color: string; size?: number }) {
  return <span aria-hidden className="inline-block rounded-full flex-shrink-0" style={{ width: size, height: size, background: color }} />;
}

interface GroupDialogsProps {
  groups: FleetGroupClient[];
  onGroups: (g: FleetGroupClient[]) => void;
  /** Fleets to assign (assign dialog) — ids of own fleets */
  fleetIds: string[];
  mode: 'manage' | 'assign';
  onClose: () => void;
  onDone?: (text: string) => void;
}

/** Manage dialog (create / rename / recolor / delete) and assign dialog (put the selected fleets in a group, or out of it). */
export function GroupDialog({ groups, onGroups, fleetIds, mode, onClose, onDone }: GroupDialogsProps) {
  const setFleets = useGameStore(s => s.setFleets);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  async function guard<T>(fn: () => Promise<T>): Promise<T | null> {
    setBusy(true); setError(null);
    try { return await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'Ismeretlen hiba'); return null; } finally { setBusy(false); }
  }
  const refreshFleets = async () => { try { setFleets((await fetchFleets()).fleets); } catch { /* ignore */ } };

  async function create() {
    const trimmed = name.trim();
    if (!trimmed) return;
    const res = await guard(() => createFleetGroup(trimmed, newIdempotencyKey('grp')));
    if (!res) return;
    onGroups(res.groups);
    setName('');
    if (mode === 'assign' && res.group && fleetIds.length > 0) await assign(res.group.id);
  }

  async function assign(groupId: string) {
    const res = await guard(() => setFleetGroupMembers(groupId, { fleetIds }));
    if (!res) return;
    onGroups(res.groups);
    await refreshFleets();
    onDone?.(`${fleetIds.length} flotta a(z) „${res.groups.find(g => g.id === groupId)?.name ?? ''}” csoportba került.`);
    onClose();
  }

  async function unassign() {
    // Take the fleets out of whichever group they are in
    const res = await guard(async () => {
      let last: FleetGroupClient[] = groups;
      for (const g of groups) {
        const ids = fleetIds.filter(id => g.fleetIds.includes(id));
        if (ids.length > 0) last = (await setFleetGroupMembers(g.id, { fleetIds: ids, remove: true })).groups;
      }
      return last;
    });
    if (!res) return;
    onGroups(res);
    await refreshFleets();
    onDone?.('A kijelölt flották kikerültek a csoportjukból.');
    onClose();
  }

  async function rename() {
    if (!editing || !editing.name.trim()) return;
    const res = await guard(() => updateFleetGroup(editing.id, { name: editing.name.trim() }));
    if (res) { onGroups(res.groups); setEditing(null); }
  }

  async function remove(g: FleetGroupClient) {
    const res = await guard(() => deleteFleetGroup(g.id));
    if (res) { onGroups(res.groups); await refreshFleets(); }
  }

  const atLimit = groups.length >= FLEET_GROUP_LIMIT;

  return (
    <ConfirmDialog
      title={mode === 'assign' ? 'Csoportba rendezés' : 'Flottacsoportok'}
      confirmLabel="Kész"
      onCancel={onClose}
      onConfirm={onClose}
    >
      <div data-testid="group-dialog" className="space-y-3">
        {mode === 'assign' && <p className="text-xs text-slate-400">{fleetIds.length} kijelölt flotta. Válassz csoportot, vagy hozz létre újat.</p>}
        <ul className="space-y-2" aria-label="Csoportok">
          {groups.length === 0 && <li className="text-xs text-slate-500">Még nincs csoportod.</li>}
          {groups.map(g => (
            <li key={g.id} className="flex items-center gap-2" data-testid="group-row" data-group-id={g.id}>
              {editing?.id === g.id ? (
                <>
                  <input aria-label="Csoport neve" className={`${INPUT} flex-1`} style={{ minHeight: 44 }} maxLength={24} value={editing.name}
                    onChange={e => setEditing({ id: g.id, name: e.target.value })} />
                  <button type="button" disabled={busy} onClick={() => void rename()} className="rounded-lg border border-cyan-500/50 text-cyan-300 text-sm px-3" style={{ minHeight: 44 }}>Mentés</button>
                </>
              ) : (
                <>
                  {mode === 'assign' ? (
                    <button type="button" disabled={busy || fleetIds.length === 0} onClick={() => void assign(g.id)} data-testid="group-assign"
                      className="flex-1 flex items-center gap-2 text-left rounded-lg border border-slate-600 px-3 text-sm text-slate-100 disabled:opacity-40" style={{ minHeight: 44 }}>
                      <GroupDot color={g.color} /> <span className="truncate">{g.name}</span>
                      <span className="ml-auto text-xs text-slate-500">{g.fleetIds.length}</span>
                    </button>
                  ) : (
                    <span className="flex-1 flex items-center gap-2 text-sm text-slate-100 min-w-0"><GroupDot color={g.color} /> <span className="truncate">{g.name}</span>
                      <span className="ml-auto text-xs text-slate-500">{g.fleetIds.length}</span></span>
                  )}
                  <button type="button" aria-label={`${g.name} átnevezése`} disabled={busy} onClick={() => setEditing({ id: g.id, name: g.name })}
                    className="rounded-lg border border-slate-600 text-slate-300 text-sm px-3" style={{ minHeight: 44, minWidth: 44 }}>✎</button>
                  <button type="button" aria-label={`${g.name} törlése`} disabled={busy} onClick={() => void remove(g)}
                    className="rounded-lg border border-red-500/40 text-red-300 text-sm px-3" style={{ minHeight: 44, minWidth: 44 }}>🗑</button>
                </>
              )}
            </li>
          ))}
        </ul>
        {mode === 'assign' && groups.some(g => g.fleetIds.some(id => fleetIds.includes(id))) && (
          <button type="button" disabled={busy} onClick={() => void unassign()} data-testid="group-unassign"
            className="w-full rounded-lg border border-slate-600 text-slate-300 text-sm" style={{ minHeight: 44 }}>Kivétel a csoportból</button>
        )}
        <div className="flex gap-2">
          <input aria-label="Új csoport neve" placeholder={atLimit ? `Legfeljebb ${FLEET_GROUP_LIMIT} csoport` : 'Új csoport neve…'} className={`${INPUT} flex-1`} style={{ minHeight: 44 }}
            maxLength={24} disabled={atLimit} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void create(); }} />
          <button type="button" disabled={busy || atLimit || !name.trim()} onClick={() => void create()} data-testid="group-create"
            className="rounded-lg border border-cyan-500/50 text-cyan-300 text-sm px-3 disabled:opacity-40" style={{ minHeight: 44 }}>
            {mode === 'assign' ? 'Létrehoz + berak' : 'Létrehoz'}
          </button>
        </div>
        {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
      </div>
    </ConfirmDialog>
  );
}
